import { camelCase, keyBy } from 'lodash';
import { useMemo } from 'react';
import { FieldSectionType } from 'api/field-sections/types';
import {
  InferredProgressOptions,
  ProjectFinancialsContractType,
  ProjectFinancialsSubscriptionFrequency,
} from 'api/project';
import { ResourceManagementPermissionsResponse } from 'api/resource-management/types';
import {
  CustomRevenueRecognitionConfigApi,
  CustomRevenueRecognitionTypes,
  FixedFeeRevenueRecognitionTypes,
  SubscriptionRevenueRecognitionTypes,
  TimeMaterialRevenueRecognitionTypes,
} from 'api/settings-financials/types';
import { FieldOptionBase } from 'api/tasks';
import { FieldDataType } from 'components/organisms/module-settings/fields/types';
import {
  FieldType,
  FilterableField,
  SourceType,
} from 'components/organisms/tasks/fields';
import { useBudgetFeature } from 'hooks/budgets/use-budget-feature';
import { useGetAllCustomerPortalsLight } from 'hooks/customer-portal/use-customer-portal-hooks';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useLocale } from 'hooks/use-language-select';
import { useIsCurrentUserPartner } from 'hooks/use-partner';
import {
  Translate,
  TranslationKey,
  useTranslate,
  useTranslation,
} from 'hooks/use-translation';
import { useProjectFieldsQuery, useProjectFieldsQueryV2 } from 'queries';
import { useCustomRevenueRecognitionQuery } from 'queries/financial-notifications';
import { useGetRevenueConfig } from 'queries/revenue-entries/revenue-config';
import { ProjectFieldsName } from 'routes/projects/list-v2/fields';
import { getRandomColor } from 'utils/get-random-color';
import { useCurrencyField } from './use-currency-field';
import { useCurrentPhasesField } from './use-current-phases-field';
import { useCustomerField } from './use-customer-field';
import { usePartnerCompanyField } from './use-partner-company-field';
import {
  useProjectOwnerField,
  useProjectOwnerFieldLite,
} from './use-project-owner-field';
import {
  useTeamMembersField,
  useTeamMembersFieldLite,
} from './use-team-members-field';
import { useTemplateField } from './use-template-field';

export const FIELD_ID_ARR = -1;
export const FIELD_ID_PROJECT_FEE = FIELD_ID_ARR - 1;
export const FIELD_ID_PROJECT_OWNER = FIELD_ID_PROJECT_FEE - 1;
const FIELD_ID_TEMPLATE = FIELD_ID_PROJECT_OWNER - 1;
const FIELD_ID_INFERRED_PROGRESS = FIELD_ID_TEMPLATE - 1;
const FIELD_ID_DUE_DATE = FIELD_ID_INFERRED_PROGRESS - 1;
const FIELD_ID_START_DATE = FIELD_ID_DUE_DATE - 1;
const FIELD_ID_LAST_STATUS_UPDATE = FIELD_ID_START_DATE - 1;
const FIELD_ID_ESTIMATED_DUE_DATE = FIELD_ID_LAST_STATUS_UPDATE - 1;

export const FIELD_ID_PROJECT_NAME = FIELD_ID_ESTIMATED_DUE_DATE - 1;
export const FIELD_ID_CUSTOMER = FIELD_ID_PROJECT_NAME - 1;
const FIELD_ID_PROGRESS_PERCENT = FIELD_ID_CUSTOMER - 1;
const FIELD_ID_TEAM_MEMBERS = FIELD_ID_PROGRESS_PERCENT - 1;
const FIELD_ID_CURRENT_PHASES = FIELD_ID_TEAM_MEMBERS - 1;
const FIELD_ID_CREATED_ON = FIELD_ID_CURRENT_PHASES - 1;
const FIELD_ID_CUSTOMER_PORTAL_ID = FIELD_ID_CREATED_ON - 1;

export const FIELD_ID_RATINGS = FIELD_ID_CUSTOMER_PORTAL_ID - 1;
const FIELD_ID_PARNTER_COMPANIES = FIELD_ID_RATINGS - 1;
const FIELD_ID_PROJECT_AGE = FIELD_ID_PARNTER_COMPANIES - 1;
const FIELD_ID_PLANNED_DURATION = FIELD_ID_PROJECT_AGE - 1;
const FIELD_ID_REMAINING_MINUTES = FIELD_ID_PLANNED_DURATION - 1;
const FIELD_ID_BUDGETED_HOURS = FIELD_ID_REMAINING_MINUTES - 1;
const FIELD_ID_TRACKED_HOURS = FIELD_ID_BUDGETED_HOURS - 1;
const FIELD_ID_ALLOCATED_HOURS = FIELD_ID_TRACKED_HOURS - 1;
const FIELD_ID_CUSTOMERS_INVITED = FIELD_ID_ALLOCATED_HOURS - 1;
const FIELD_ID_CUSTOMERS_ACCEPTED = FIELD_ID_CUSTOMERS_INVITED - 1;
const FIELD_ID_PROJECT_BUDGET = FIELD_ID_CUSTOMERS_ACCEPTED - 1;

const FIELD_ID_FINANCIAL_CONTRACT_TYPE = FIELD_ID_PROJECT_BUDGET - 1;
const FIELD_ID_ESTIMATE_AT_COMPLETION = FIELD_ID_FINANCIAL_CONTRACT_TYPE - 1;
const FIELD_ID_ESTIMATE_TO_COMPLETION = FIELD_ID_ESTIMATE_AT_COMPLETION - 1;
const FIELD_ID_CURRENCY = FIELD_ID_ESTIMATE_TO_COMPLETION - 1;
const FIELD_ID_ACTUAL_REVENUE = FIELD_ID_CURRENCY - 1;
const FIELD_ID_ACTUAL_COST = FIELD_ID_ACTUAL_REVENUE - 1;
const FIELD_ID_ACTUAL_PROFIT = FIELD_ID_ACTUAL_COST - 1;
const FIELD_ID_ACTUAL_MARGIN = FIELD_ID_ACTUAL_PROFIT - 1;
const FIELD_ID_PERCENTAGE_BUDGETED_HOURS_CONSUMED = FIELD_ID_ACTUAL_MARGIN - 1;
const FIELD_ID_PERCENTAGE_BUDGET_CONSUMED =
  FIELD_ID_PERCENTAGE_BUDGETED_HOURS_CONSUMED - 1;
const FIELD_ID_SUBSCRIPTION_FREQUENCY = FIELD_ID_PERCENTAGE_BUDGET_CONSUMED - 1;
const FIELD_ID_NUMBER_OF_PERIODS = FIELD_ID_SUBSCRIPTION_FREQUENCY - 1;
const FIELD_ID_REVENUE_RECOGNITION_TYPE = FIELD_ID_NUMBER_OF_PERIODS - 1;
const FIELD_ID_CURRENT_PERIOD = FIELD_ID_REVENUE_RECOGNITION_TYPE - 1;
const FIELD_ID_CURRENT_PERIOD_BUDGET = FIELD_ID_CURRENT_PERIOD - 1;
const FIELD_ID_CURRENT_PERIOD_BUDGET_HOURS = FIELD_ID_CURRENT_PERIOD_BUDGET - 1;
const FIELD_ID_CURRENT_PERIOD_START_DATE =
  FIELD_ID_CURRENT_PERIOD_BUDGET_HOURS - 1;
const FIELD_ID_CURRENT_PERIOD_END_DATE = FIELD_ID_CURRENT_PERIOD_START_DATE - 1;
const FIELD_ID_CURRENT_PERIOD_REVENUE = FIELD_ID_CURRENT_PERIOD_END_DATE - 1;
const FIELD_ID_CURRENT_PERIOD_TRACKED_HOURS =
  FIELD_ID_CURRENT_PERIOD_REVENUE - 1;
const FIELD_ID_RECOGNISED_REVENUE = FIELD_ID_CURRENT_PERIOD_TRACKED_HOURS - 1;
const FIELD_ID_LAST_MILESTONE_CSAT = FIELD_ID_RECOGNISED_REVENUE - 1;

export const ProjectFinancialsContractTypeOptions = (
  translate: Translate,
): FieldOptionBase<ProjectFinancialsContractType>[] => [
  {
    label: translate('project.settings.financials.billing_type.FIXED_FEE'),
    value: ProjectFinancialsContractType.FIXED,
    color: 'green',
    active: true,
  },
  {
    label: translate(
      'project.settings.financials.billing_type.TIME_AND_MATERIAL',
    ),
    value: ProjectFinancialsContractType.TM,
    color: 'blue',
    active: true,
  },
  {
    label: translate('project.settings.financials.billing_type.NON_BILLABLE'),
    value: ProjectFinancialsContractType.NONBILLABLE,
    color: 'gray',
    active: true,
  },
  {
    label: translate('project.settings.financials.billing_type.SUBSCRIPTION'),
    value: ProjectFinancialsContractType.SUBSCRIPTION,
    color: 'purple',
    active: true,
  },
];

const getRevenueRecognitionOptions = (
  translate: Translate<string, string>,
  customRevenueRecognitionConfigs?: Record<
    ProjectFinancialsContractType,
    CustomRevenueRecognitionConfigApi[]
  >,
) =>
  Object.values(FixedFeeRevenueRecognitionTypes)
    .map((value) => ({
      label: translate(
        `settings.financials.revenue_recognition.${value}` as TranslationKey,
      ),
      value: value.toString(),
      active: true,
      color: getRandomColor(value.toString()),
    }))
    .concat(
      Object.values(SubscriptionRevenueRecognitionTypes).map((value) => ({
        label: translate(
          `settings.financials.revenue_recognition.${value}` as TranslationKey,
        ),
        value: value.toString(),
        active: true,
        color: getRandomColor(value.toString()),
      })),
    )
    .concat(
      Object.values(TimeMaterialRevenueRecognitionTypes).map((value) => ({
        label: translate(
          `settings.financials.revenue_recognition.${value}` as TranslationKey,
        ),
        value: value.toString(),
        active: true,
        color: getRandomColor(value.toString()),
      })),
    )
    .concat(
      customRevenueRecognitionConfigs
        ? Object.values(customRevenueRecognitionConfigs)
            .flat()
            .map((config) => ({
              label: config.name,
              // Setting value this way because of filters. Filters can't handle string value & number value for the same field, even if you stringify the number.
              // You'll find the same in transformer for project list data.
              value: `${CustomRevenueRecognitionTypes.CustomRevenueRecognition}_${config.customRevenueRecognitionConfigId}`,
              active: true,
              color: getRandomColor(config.customRevenueRecognitionConfigId),
            }))
        : [],
    );

export const ProjectFinancialsSubscriptionFrequencyOptions = (
  translate: Translate,
): FieldOptionBase<ProjectFinancialsSubscriptionFrequency>[] => [
  {
    label: translate(
      'projects.create.financials.SUBSCRIPTION.frequency.MONTHLY',
    ),
    value: ProjectFinancialsSubscriptionFrequency.Monthly,
    color: 'green',
    active: true,
  },
  {
    label: translate(
      'projects.create.financials.SUBSCRIPTION.frequency.QUARTERLY',
    ),
    value: ProjectFinancialsSubscriptionFrequency.Quarterly,
    color: 'blue',
    active: true,
  },
  {
    label: translate(
      'projects.create.financials.SUBSCRIPTION.frequency.HALF_YEARLY.title_case',
    ),
    value: ProjectFinancialsSubscriptionFrequency.HalfYearly,
    color: 'red',
    active: true,
  },
  {
    label: translate(
      'projects.create.financials.SUBSCRIPTION.frequency.YEARLY',
    ),
    value: ProjectFinancialsSubscriptionFrequency.Yearly,
    color: 'purple',
    active: true,
  },
];

export const ProjectFinancialsRevenueRecognitionTypeOptions = (
  translate: Translate,
  customRevenueRecognitionConfigs?: Record<
    ProjectFinancialsContractType,
    CustomRevenueRecognitionConfigApi[]
  >,
): FieldOptionBase<
  | FixedFeeRevenueRecognitionTypes
  | TimeMaterialRevenueRecognitionTypes
  | SubscriptionRevenueRecognitionTypes
  | string
>[] => [
  ...Object.values(FixedFeeRevenueRecognitionTypes).map((value) => ({
    label: translate(
      `settings.financials.revenue_recognition.${value}` as TranslationKey,
    ),
    value,
    color: getRandomColor(value),
    active: true,
  })),
  ...Object.values(TimeMaterialRevenueRecognitionTypes).map((value) => ({
    label: translate(
      `settings.financials.revenue_recognition.${value}` as TranslationKey,
    ),
    value,
    color: getRandomColor(value),
    active: true,
  })),
  ...Object.values(SubscriptionRevenueRecognitionTypes).map((value) => ({
    label: translate(
      `settings.financials.revenue_recognition.${value}` as TranslationKey,
    ),
    value,
    color: getRandomColor(value),
    active: true,
  })),
  ...(customRevenueRecognitionConfigs
    ? Object.values(customRevenueRecognitionConfigs)
        .flat()
        .map((config) => ({
          label: config.name,
          // Setting value this way because of filters. Filters can't handle string value & number value for the same field, even if you stringify the number.
          // You'll find the same in transformer for project list data.
          value: `${CustomRevenueRecognitionTypes.CustomRevenueRecognition}_${config.customRevenueRecognitionConfigId}`,
          active: true,
          color: getRandomColor(config.customRevenueRecognitionConfigId),
        }))
    : []),
];

export const ProjectTimeTrackingFieldsName = {
  trackedHours: 'totalMinutes',
  allocatedHours: 'totalAllocatedHours',
  budgetedMinutes: 'budgetedMinutes',
  remainingMinutes: 'remainingMinutes',
  billableMinutes: 'billableMinutes',
  periodMinutes: 'periodMinutes',
  periodTotalTimeTracked: 'periodTotalTimeTracked',
  projectFinancialsBudgets: 'projectFinancialsBudgets',
};

/**
 * @deprecated Use useProjectNativeFieldsAsFilterableFieldsLite instead
 */
export const useProjectNativeFieldsAsFilterableFields = (
  {
    peopleIHaveAccessTo,
    includeCurrentPeriodDetailsFilterFields,
  }: {
    peopleIHaveAccessTo?: ResourceManagementPermissionsResponse;
    includeCurrentPeriodDetailsFilterFields?: boolean;
  } = {
    peopleIHaveAccessTo: undefined,
  },
): {
  loading: boolean;
  fields: FilterableField[];
} => {
  const { data: customerPortals } = useGetAllCustomerPortalsLight();
  const translate = useTranslate();
  const { locale } = useLocale();
  const isCurrentUserPartner = useIsCurrentUserPartner();
  const arr_label = useTranslation('project.fields.arr');
  const project_name_label = useTranslation('project.fields.project_name');
  const project_fee_label = useTranslation('project.fields.project_fee');
  const due_date_label = useTranslation('due_date');
  const start_date_label = useTranslation('start_date');
  const last_status_update_label = useTranslation(
    'project.fields.last_status_update',
  );
  const [isTimeSheetEnabled] = useAccountFeature(AccountFeature.Timesheets);
  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);
  const isMultiLineBudgetsEnabled = useBudgetFeature();

  const { data: revenueConfig } = useGetRevenueConfig();
  const isRevenueFeatureEnabled = revenueConfig?.enabled;

  const progress_percent_label = useTranslation(
    'project.fields.progress_percent',
  );
  const inferred_progress_label = useTranslation(
    'project.fields.inferred_progress',
  );
  const project_age_label = useTranslation('project.fields.project_age');
  const planned_duration_label = useTranslation(
    'project.fields.planned_duration',
  );
  const trackedHoursLabel = useTranslation('project.fields.tracked_hours');
  const budgetHoursLabel = useTranslation('project.fields.budgeted_hours');
  const remainingMinutesLabel = useTranslation(
    'project.fields.remaining_minutes',
  );
  const allocatedHoursLabel = useTranslation('project.fields.allocated_hours');
  const customersInvitedLabel = useTranslation(
    'project.fields.customers_invited',
  );
  const customersAcceptedLabel = useTranslation(
    'project.fields.customers_accepted',
  );
  const projectCreatedOnLabel = useTranslation('project.fields.created_on');
  const projectBudgetLabel = useTranslation('project.fields.project_budget');
  const actualRevenueLabel = useTranslation(
    isRevenueFeatureEnabled
      ? 'project.fields.system_revenue'
      : 'project.fields.actual_revenue',
  );
  const recognisedRevenueLabel = useTranslation(
    'project.fields.recognised_revenue',
  );
  const actualCostLabel = useTranslation('project.fields.actual_cost');
  const actualProfitLabel = useTranslation('project.fields.actual_profit');
  const actualProfitMarginLabel = useTranslation(
    'project.fields.actual_profit_margin',
  );
  const estimateAtCompletionLabel = useTranslation(
    'project.fields.estimate_at_completion',
  );
  const estimateToCompletionLabel = useTranslation(
    'project.fields.estimate_to_completion',
  );
  const financialContractTypeLabel = useTranslation(
    'project.fields.financial_contract_type',
  );
  const revenueRecognitionTypeLabel = useTranslation(
    'project.fields.revenue_recognition_type',
  );
  const percentageBudgetedHoursConsumedLabel = useTranslation(
    'project.fields.percentage_budgeted_hours_consumed',
  );
  const percentageBudgetConsumedLabel = useTranslation(
    'project.fields.percentage_budget_consumed',
  );
  const subscriptionFrequencyLabel = useTranslation(
    'project.fields.subscription_frequency',
  );
  const numberOfPeriodsLabel = useTranslation(
    'project.fields.number_of_periods',
  );
  const currentPeriodLabel = useTranslation('project.fields.current_period');
  const periodBudgetLabel = useTranslation('project.fields.period_budget');
  const periodBudgetedMinutesLabel = useTranslation(
    'project.fields.period_budgeted_hours',
  );
  const currentPeriodRevenueLabel = useTranslation(
    'project.fields.current_period_revenue',
  );
  const currentPeriodTrackedHoursLabel = useTranslation(
    'project.fields.current_period_tracked_hours',
  );
  const currentPeriodStartDateLabel = useTranslation(
    'project.fields.current_period_start_date',
  );
  const currentPeriodEndDateLabel = useTranslation(
    'project.fields.current_period_end_date',
  );

  const { data: projectFieldsData, isLoading: isProjectFieldsLoading } =
    useProjectFieldsQuery();

  const { hasAccessToResource } = useWithAccess();

  const canAccessProfitability = hasAccessToResource({
    rule: 'PROJECT_PROFITABILITY:READ',
  });

  const canViewCostRates = useMemo(
    () => isFinancialsEnabled && canAccessProfitability,
    [canAccessProfitability, isFinancialsEnabled],
  );
  const { data: customRevenueRecognitionConfigs } =
    useCustomRevenueRecognitionQuery(false, {
      enabled: isFinancialsEnabled && !isCurrentUserPartner,
    });

  const projectFieldsMapByName = useMemo(
    () => keyBy(projectFieldsData ?? [], 'fieldName'),
    [projectFieldsData],
  );

  // Helper function to get label from backend response or fallback to translation
  const getFieldLabel = useMemo(
    () =>
      (backendFieldName: string, fallbackLabel: string): string => {
        if (isProjectFieldsLoading) {
          return fallbackLabel;
        }

        const backendField = projectFieldsMapByName[backendFieldName];

        return backendField?.fieldLabel ?? fallbackLabel;
      },
    [projectFieldsMapByName, isProjectFieldsLoading],
  );

  const arrProjectFee = useMemo(() => {
    if (isProjectFieldsLoading) {
      return [];
    }
    const fields = projectFieldsData?.map((field) => {
      if (field.fieldName === 'annualizedRecurringRevenue' && field.isEnabled) {
        return {
          fieldName: 'arr',
          fieldLabel: getFieldLabel('annualizedRecurringRevenue', arr_label),
          type: FieldType.Number,
          fieldId: FIELD_ID_ARR,
          fieldIndex: field.fieldIndex,
        };
      }
      if (field.fieldName === 'projectFee' && field.isEnabled) {
        return {
          fieldName: 'projectFee',
          fieldLabel: getFieldLabel('projectFee', project_fee_label),
          type: FieldType.Number,
          fieldId: FIELD_ID_PROJECT_FEE,
          fieldIndex: field.fieldIndex,
        };
      }

      return null;
    });

    if (fields) {
      return fields.filter(Boolean);
    }

    return [];
  }, [
    arr_label,
    project_fee_label,
    projectFieldsData,
    isProjectFieldsLoading,
    getFieldLabel,
  ]);

  const projectFields = useMemo(
    () =>
      [
        {
          fieldName: 'projectName',
          fieldLabel: getFieldLabel('projectName', project_name_label),
          type: FieldType.Text,
          fieldId: FIELD_ID_PROJECT_NAME,
        },
        {
          fieldName: 'inferredProgress',
          fieldLabel: getFieldLabel(
            'inferredProgress',
            inferred_progress_label,
          ),
          type: FieldType.SingleSelect,
          fieldId: FIELD_ID_INFERRED_PROGRESS,

          optionsAvailable: true,
          metaData: {
            options: InferredProgressOptions(translate),
          },
        },
        {
          fieldName: 'dueDate',
          fieldLabel: getFieldLabel('dueDate', due_date_label),
          type: FieldType.Date,
          fieldId: FIELD_ID_DUE_DATE,
        },
        {
          fieldName: 'startDate',
          fieldLabel: getFieldLabel('startDate', start_date_label),
          type: FieldType.Date,
          fieldId: FIELD_ID_START_DATE,
        },
        {
          fieldLabel: getFieldLabel(
            'lastStatusUpdate',
            last_status_update_label,
          ),
          fieldName: 'lastStatusUpdate',
          type: FieldType.Date,
          fieldId: FIELD_ID_LAST_STATUS_UPDATE,
        },
        {
          fieldLabel: getFieldLabel('progressPercent', progress_percent_label),
          fieldName: ProjectFieldsName.progressPercent,
          type: FieldType.Number,
          fieldId: FIELD_ID_PROGRESS_PERCENT,
        },
        {
          fieldLabel: getFieldLabel('projectAge', project_age_label),
          fieldName: 'projectAge',
          type: FieldType.Number,
          fieldId: FIELD_ID_PROJECT_AGE,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: getFieldLabel('plannedDuration', planned_duration_label),
          fieldName: 'plannedDuration',
          type: FieldType.Number,
          fieldId: FIELD_ID_PLANNED_DURATION,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: getFieldLabel(
            'customerMembersInvited',
            customersInvitedLabel,
          ),
          fieldName: 'customerMembersInvited',
          type: FieldType.Number,
          fieldId: FIELD_ID_CUSTOMERS_INVITED,
        },
        {
          fieldLabel: getFieldLabel(
            'customerMembersAccepted',
            customersAcceptedLabel,
          ),
          fieldName: 'customerMembersAccepted',
          type: FieldType.Number,
          fieldId: FIELD_ID_CUSTOMERS_ACCEPTED,
        },
        {
          fieldLabel: getFieldLabel('createdAt', projectCreatedOnLabel),
          fieldName: 'createdAt',
          type: FieldType.Date,
          fieldId: FIELD_ID_CREATED_ON,
        },
        {
          fieldName: 'customerPortalId',
          fieldLabel: translate('templates.customer_portal_singular'),
          type: FieldType.SingleSelect,
          fieldId: FIELD_ID_CUSTOMER_PORTAL_ID,
          fieldType: FieldType.SingleSelect,
          fieldDataType: FieldDataType.Number,
          optionsAvailable: true,
          metaData: {
            options: customerPortals?.map((x) => ({
              label: x.customerPortalName,
              value: x.customerPortalId,
              active: true,
              color: getRandomColor(x?.customerPortalName),
            })),
          },
        },
        {
          fieldLabel: translate('last_milestone_rating'),
          fieldName: ProjectFieldsName.lastMilestoneCSAT,
          type: FieldType.Number,
          fieldId: FIELD_ID_LAST_MILESTONE_CSAT,
        },
        ...arrProjectFee,
        ...(isFinancialsEnabled && !isCurrentUserPartner
          ? [
              {
                fieldLabel: getFieldLabel('projectBudget', projectBudgetLabel),
                fieldName: 'projectBudget',
                type: FieldType.Number,
                fieldId: FIELD_ID_PROJECT_BUDGET,
              },
              ...(!isMultiLineBudgetsEnabled
                ? [
                    {
                      fieldLabel: getFieldLabel(
                        'financialContractType',
                        financialContractTypeLabel,
                      ),
                      fieldName: 'financialContractType',
                      type: FieldType.SingleSelect,
                      fieldId: FIELD_ID_FINANCIAL_CONTRACT_TYPE,
                      optionsAvailable: true,
                      metaData: {
                        options:
                          ProjectFinancialsContractTypeOptions(translate),
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'revenueRecognitionType',
                        revenueRecognitionTypeLabel,
                      ),
                      fieldName: 'revenueRecognitionType',
                      type: FieldType.SingleSelect,
                      fieldId: FIELD_ID_REVENUE_RECOGNITION_TYPE,
                      optionsAvailable: true,
                      metaData: {
                        options: getRevenueRecognitionOptions(
                          translate as Translate<string, string>,
                          customRevenueRecognitionConfigs,
                        ),
                      },
                    },
                  ]
                : []),
              {
                fieldLabel: getFieldLabel('actualRevenue', actualRevenueLabel),
                fieldName: 'actualRevenue',
                type: FieldType.Number,
                fieldId: FIELD_ID_ACTUAL_REVENUE,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: getFieldLabel(
                  'totalRecognisedRevenue',
                  recognisedRevenueLabel,
                ),
                fieldName: 'totalRecognisedRevenue',
                type: FieldType.Number,
                fieldId: FIELD_ID_RECOGNISED_REVENUE,
              },
              {
                fieldLabel: getFieldLabel(
                  'percentageBudgetConsumed',
                  percentageBudgetConsumedLabel,
                ),
                fieldName: 'percentageBudgetConsumed',
                type: FieldType.Number,
                fieldId: FIELD_ID_PERCENTAGE_BUDGET_CONSUMED,
              },
              ...(!isMultiLineBudgetsEnabled
                ? [
                    {
                      fieldLabel: getFieldLabel(
                        'subscriptionFrequency',
                        subscriptionFrequencyLabel,
                      ),
                      fieldName: 'subscriptionFrequency',
                      type: FieldType.SingleSelect,
                      fieldId: FIELD_ID_SUBSCRIPTION_FREQUENCY,
                      optionsAvailable: true,
                      metaData: {
                        options:
                          ProjectFinancialsSubscriptionFrequencyOptions(
                            translate,
                          ),
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'noOfPeriods',
                        numberOfPeriodsLabel,
                      ),
                      fieldName: 'numberOfPeriods',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_NUMBER_OF_PERIODS,
                      metaData: {
                        required: true,
                      },
                    },
                  ]
                : []),
              ...(includeCurrentPeriodDetailsFilterFields &&
              !isMultiLineBudgetsEnabled
                ? [
                    {
                      fieldLabel: getFieldLabel(
                        'currentPeriodNumber',
                        currentPeriodLabel,
                      ),
                      fieldName: 'currentPeriodNumber',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'currentPeriodStartDate',
                        currentPeriodStartDateLabel,
                      ),
                      fieldName: 'currentPeriodStartDate',
                      type: FieldType.Date,
                      fieldId: FIELD_ID_CURRENT_PERIOD_START_DATE,
                      metaData: {
                        required: true,
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'currentPeriodEndDate',
                        currentPeriodEndDateLabel,
                      ),
                      fieldName: 'currentPeriodEndDate',
                      type: FieldType.Date,
                      fieldId: FIELD_ID_CURRENT_PERIOD_END_DATE,
                      metaData: {
                        required: true,
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'periodBudget',
                        periodBudgetLabel,
                      ),
                      fieldName: 'periodBudget',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD_BUDGET,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'periodMinutes',
                        periodBudgetedMinutesLabel,
                      ),
                      fieldName: 'periodMinutes',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD_BUDGET_HOURS,
                      metaData: {
                        required: true,
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'periodRevenue',
                        currentPeriodRevenueLabel,
                      ),
                      fieldName: 'periodRevenue',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD_REVENUE,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'periodTotalTimeTracked',
                        currentPeriodTrackedHoursLabel,
                      ),
                      fieldName: 'periodTotalTimeTracked',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD_TRACKED_HOURS,
                    },
                  ]
                : []),
              ...(canViewCostRates
                ? [
                    {
                      fieldLabel: getFieldLabel('actualCost', actualCostLabel),
                      fieldName: 'actualCost',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_COST,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'actualProfit',
                        actualProfitLabel,
                      ),
                      fieldName: 'actualProfit',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_PROFIT,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'actualProfitMargin',
                        actualProfitMarginLabel,
                      ),
                      fieldName: 'actualProfitMargin',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_MARGIN,
                    },
                  ]
                : []),
            ]
          : []),
        ...(isTimeSheetEnabled && !isCurrentUserPartner
          ? [
              {
                fieldLabel: getFieldLabel('totalMinutes', trackedHoursLabel),
                fieldName: ProjectTimeTrackingFieldsName.trackedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_TRACKED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel:
                  locale?.toLowerCase() === 'en'
                    ? budgetHoursLabel
                    : getFieldLabel('budgetedMinutes', budgetHoursLabel),
                fieldName: ProjectTimeTrackingFieldsName.budgetedMinutes,
                type: FieldType.Number,
                fieldId: FIELD_ID_BUDGETED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: getFieldLabel(
                  'remainingMinutes',
                  remainingMinutesLabel,
                ),
                fieldName: ProjectTimeTrackingFieldsName.remainingMinutes,
                type: FieldType.Number,
                fieldId: FIELD_ID_REMAINING_MINUTES,
              },
              {
                fieldLabel: getFieldLabel(
                  'totalAllocatedHours',
                  allocatedHoursLabel,
                ),
                fieldName: ProjectTimeTrackingFieldsName.allocatedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_ALLOCATED_HOURS,
              },
              {
                fieldLabel: getFieldLabel(
                  'estimateAtCompletion',
                  estimateAtCompletionLabel,
                ),
                fieldName: 'estimateAtCompletion',
                type: FieldType.Number,
                fieldId: FIELD_ID_ESTIMATE_AT_COMPLETION,
              },
              {
                fieldLabel: getFieldLabel(
                  'estimateToComplete',
                  estimateToCompletionLabel,
                ),
                fieldName: 'estimateToComplete',
                type: FieldType.Number,
                fieldId: FIELD_ID_ESTIMATE_TO_COMPLETION,
              },
              {
                fieldLabel: getFieldLabel(
                  'percentageBudgetedHoursConsumed',
                  percentageBudgetedHoursConsumedLabel,
                ),
                fieldName: 'percentageBudgetedHoursConsumed',
                type: FieldType.Number,
                fieldId: FIELD_ID_PERCENTAGE_BUDGETED_HOURS_CONSUMED,
              },
            ]
          : []),
      ].map(
        (x) =>
          ({
            ...x,
            customField: false,
            private: false,
            sourceType: SourceType.Project,
            fieldIndex:
              projectFieldsMapByName[camelCase(x?.fieldName)]?.fieldIndex,
            objectType: FieldSectionType.Project,
          } as FilterableField),
      ),
    [
      getFieldLabel,
      project_name_label,
      inferred_progress_label,
      due_date_label,
      start_date_label,
      last_status_update_label,
      progress_percent_label,
      project_age_label,
      planned_duration_label,
      customersInvitedLabel,
      customersAcceptedLabel,
      projectCreatedOnLabel,
      customerPortals,
      arrProjectFee,
      isFinancialsEnabled,
      isCurrentUserPartner,
      isMultiLineBudgetsEnabled,
      projectBudgetLabel,
      financialContractTypeLabel,
      revenueRecognitionTypeLabel,
      translate,
      customRevenueRecognitionConfigs,
      actualRevenueLabel,
      recognisedRevenueLabel,
      percentageBudgetConsumedLabel,
      subscriptionFrequencyLabel,
      numberOfPeriodsLabel,
      includeCurrentPeriodDetailsFilterFields,
      currentPeriodLabel,
      currentPeriodStartDateLabel,
      currentPeriodEndDateLabel,
      periodBudgetLabel,
      periodBudgetedMinutesLabel,
      currentPeriodRevenueLabel,
      currentPeriodTrackedHoursLabel,
      canViewCostRates,
      actualCostLabel,
      actualProfitLabel,
      actualProfitMarginLabel,
      isTimeSheetEnabled,
      trackedHoursLabel,
      budgetHoursLabel,
      remainingMinutesLabel,
      allocatedHoursLabel,
      estimateAtCompletionLabel,
      estimateToCompletionLabel,
      percentageBudgetedHoursConsumedLabel,
      projectFieldsMapByName,
      locale,
    ],
  );
  const { templateField, isLoading: isTemplateFieldLoading } =
    useTemplateField(FIELD_ID_TEMPLATE);
  const { projectOwnerField, isLoading: isProjectOwnerFieldLoading } =
    useProjectOwnerField(FIELD_ID_PROJECT_OWNER);
  const { customerField, isLoading: isCustomerFieldLoading } =
    useCustomerField(FIELD_ID_CUSTOMER);
  const { teamMemberField, isLoading: isTeamMembersFieldLoading } =
    useTeamMembersField(FIELD_ID_TEAM_MEMBERS, peopleIHaveAccessTo);
  const { currentPhasesField, isLoading: isCurrentPhasesFieldLoading } =
    useCurrentPhasesField(FIELD_ID_CURRENT_PHASES);
  const {
    partnerCompaniesField,
    isLoading: isPartnerCompaniesFieldLoading,
    isAnyPartnerCompanyAvailable,
  } = usePartnerCompanyField(FIELD_ID_PARNTER_COMPANIES);
  const { currencyField, isLoading: isCurrencyFieldLoading } =
    useCurrencyField(FIELD_ID_CURRENCY);

  const fields = useMemo(
    () =>
      [
        ...projectFields,
        templateField,
        projectOwnerField,
        customerField,
        teamMemberField,
        currentPhasesField,
        isAnyPartnerCompanyAvailable ? partnerCompaniesField : undefined,
        currencyField,
      ].filter((x) => Boolean(x)) as FilterableField[],
    [
      projectFields,
      templateField,
      projectOwnerField,
      customerField,
      teamMemberField,
      currentPhasesField,
      isAnyPartnerCompanyAvailable,
      partnerCompaniesField,
      currencyField,
    ],
  );

  const isFieldsLoading = useMemo(
    () =>
      isProjectOwnerFieldLoading ||
      isCustomerFieldLoading ||
      isTeamMembersFieldLoading ||
      isCurrentPhasesFieldLoading ||
      isTemplateFieldLoading ||
      isPartnerCompaniesFieldLoading ||
      isCurrencyFieldLoading,
    [
      isProjectOwnerFieldLoading,
      isCustomerFieldLoading,
      isTeamMembersFieldLoading,
      isCurrentPhasesFieldLoading,
      isTemplateFieldLoading,
      isPartnerCompaniesFieldLoading,
      isCurrencyFieldLoading,
    ],
  );

  return useMemo(
    () => ({
      fields,
      loading: isFieldsLoading,
    }),
    [fields, isFieldsLoading],
  );
};

export const useProjectNativeFieldsForVariables = () => {
  const { fields, loading } = useProjectNativeFieldsAsFilterableFields();

  return useMemo(
    () => ({
      loading,
      fields: fields.filter(({ fieldId }) =>
        [
          FIELD_ID_PROJECT_NAME,
          FIELD_ID_START_DATE,
          FIELD_ID_DUE_DATE,
          FIELD_ID_ARR,
          FIELD_ID_PROJECT_FEE,
          FIELD_ID_BUDGETED_HOURS,
        ].includes(fieldId),
      ),
    }),
    [fields, loading],
  );
};

/**
 * Lightweight version of useProjectNativeFieldsAsFilterableFields.
 * Eliminates expensive API calls by using lite field hooks.
 *
 * Key differences from the original:
 * - Uses useTeamMembersFieldLite (no /members API call)
 * - Uses useProjectOwnerFieldLite (no /members API call)
 * - Omits peopleIHaveAccessTo (RBAC filtering)
 * - Includes includeCurrentPeriodDetailsFilterFields (same as original)
 *
 * Use this for:
 * - Dashboards, grids, and filter views where user data is fetched on-demand
 * - usePaginatedProjectFields (project list grid)
 * - Flows with projectId context where project-scoped APIs are available
 *
 * DO NOT use this for:
 * - Views requiring upfront RBAC filtering (peopleIHaveAccessTo)
 */
export const useProjectNativeFieldsLite = ({
  includeCurrentPeriodDetailsFilterFields,
  includedFieldNames,
}: {
  includeCurrentPeriodDetailsFilterFields?: boolean;
  includedFieldNames?: string[];
} = {}): {
  loading: boolean;
  fields: FilterableField[];
} => {
  const { data: customerPortals } = useGetAllCustomerPortalsLight();
  const translate = useTranslate();
  const { locale } = useLocale();
  const isCurrentUserPartner = useIsCurrentUserPartner();
  const arr_label = useTranslation('project.fields.arr');
  const project_name_label = useTranslation('project.fields.project_name');
  const project_fee_label = useTranslation('project.fields.project_fee');
  const due_date_label = useTranslation('due_date');
  const start_date_label = useTranslation('start_date');
  const last_status_update_label = useTranslation(
    'project.fields.last_status_update',
  );
  const [isTimeSheetEnabled] = useAccountFeature(AccountFeature.Timesheets);
  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);

  const { data: revenueConfig } = useGetRevenueConfig();
  const isRevenueFeatureEnabled = revenueConfig?.enabled;

  const progress_percent_label = useTranslation(
    'project.fields.progress_percent',
  );
  const inferred_progress_label = useTranslation(
    'project.fields.inferred_progress',
  );
  const project_age_label = useTranslation('project.fields.project_age');
  const planned_duration_label = useTranslation(
    'project.fields.planned_duration',
  );
  const trackedHoursLabel = useTranslation('project.fields.tracked_hours');
  const budgetHoursLabel = useTranslation('project.fields.budgeted_hours');
  const remainingMinutesLabel = useTranslation(
    'project.fields.remaining_minutes',
  );
  const allocatedHoursLabel = useTranslation('project.fields.allocated_hours');
  const customersInvitedLabel = useTranslation(
    'project.fields.customers_invited',
  );
  const customersAcceptedLabel = useTranslation(
    'project.fields.customers_accepted',
  );
  const projectCreatedOnLabel = useTranslation('project.fields.created_on');
  const projectBudgetLabel = useTranslation('project.fields.project_budget');
  const actualRevenueLabel = useTranslation(
    isRevenueFeatureEnabled
      ? 'project.fields.system_revenue'
      : 'project.fields.actual_revenue',
  );
  const recognisedRevenueLabel = useTranslation(
    'project.fields.recognised_revenue',
  );
  const actualCostLabel = useTranslation('project.fields.actual_cost');
  const actualProfitLabel = useTranslation('project.fields.actual_profit');
  const actualProfitMarginLabel = useTranslation(
    'project.fields.actual_profit_margin',
  );
  const estimateAtCompletionLabel = useTranslation(
    'project.fields.estimate_at_completion',
  );
  const estimateToCompletionLabel = useTranslation(
    'project.fields.estimate_to_completion',
  );
  const financialContractTypeLabel = useTranslation(
    'project.fields.financial_contract_type',
  );
  const revenueRecognitionTypeLabel = useTranslation(
    'project.fields.revenue_recognition_type',
  );
  const percentageBudgetedHoursConsumedLabel = useTranslation(
    'project.fields.percentage_budgeted_hours_consumed',
  );
  const percentageBudgetConsumedLabel = useTranslation(
    'project.fields.percentage_budget_consumed',
  );
  const subscriptionFrequencyLabel = useTranslation(
    'project.fields.subscription_frequency',
  );
  const numberOfPeriodsLabel = useTranslation(
    'project.fields.number_of_periods',
  );
  const currentPeriodLabel = useTranslation('project.fields.current_period');
  const periodBudgetLabel = useTranslation('project.fields.period_budget');
  const periodBudgetedMinutesLabel = useTranslation(
    'project.fields.period_budgeted_hours',
  );
  const currentPeriodStartDateLabel = useTranslation(
    'project.fields.current_period_start_date',
  );
  const currentPeriodEndDateLabel = useTranslation(
    'project.fields.current_period_end_date',
  );
  const currentPeriodRevenueLabel = useTranslation(
    'project.fields.current_period_revenue',
  );
  const currentPeriodTrackedHoursLabel = useTranslation(
    'project.fields.current_period_tracked_hours',
  );

  const isMultiLineBudgetsEnabled = useBudgetFeature();

  const financialContractTypeField = useMemo(
    () =>
      ({
        fieldId: FIELD_ID_FINANCIAL_CONTRACT_TYPE,
        fieldName: ProjectFieldsName.financialContractType,
        fieldLabel: translate('project.fields.financial_contract_type'),
        type: FieldType.SingleSelect,
        optionsAvailable: true,
        metaData: {
          options: ProjectFinancialsContractTypeOptions(translate),
        },
        customField: false,
        userCreated: false,
        private: false,
        sourceType: SourceType.Project,
      } as unknown as FilterableField),
    [translate],
  );

  const { data: projectFieldsDataResp, isLoading: isProjectFieldsLoading } =
    useProjectFieldsQueryV2();

  const projectFieldsData = useMemo(() => {
    return projectFieldsDataResp?.fields ?? [];
  }, [projectFieldsDataResp]);

  const { hasAccessToResource } = useWithAccess();

  const canAccessProfitability = hasAccessToResource({
    rule: 'PROJECT_PROFITABILITY:READ',
  });

  const canViewCostRates = useMemo(
    () => isFinancialsEnabled && canAccessProfitability,
    [canAccessProfitability, isFinancialsEnabled],
  );
  const { data: customRevenueRecognitionConfigs } =
    useCustomRevenueRecognitionQuery(false, {
      enabled: isFinancialsEnabled && !isCurrentUserPartner,
    });

  const projectFieldsMapByName = useMemo(
    () => keyBy(projectFieldsData ?? [], 'fieldName'),
    [projectFieldsData],
  );

  const getFieldLabel = useMemo(
    () =>
      (backendFieldName: string, fallbackLabel: string): string => {
        if (isProjectFieldsLoading) {
          return fallbackLabel;
        }

        const backendField = projectFieldsMapByName[backendFieldName];

        return backendField?.fieldLabel ?? fallbackLabel;
      },
    [projectFieldsMapByName, isProjectFieldsLoading],
  );

  const arrProjectFee = useMemo(() => {
    if (isProjectFieldsLoading) {
      return [];
    }
    const fields = projectFieldsData?.map((field) => {
      if (field.fieldName === 'annualizedRecurringRevenue' && field.isEnabled) {
        return {
          fieldName: 'arr',
          fieldLabel: getFieldLabel('annualizedRecurringRevenue', arr_label),
          type: FieldType.Number,
          fieldId: FIELD_ID_ARR,
          fieldIndex: field.fieldIndex,
        };
      }
      if (field.fieldName === 'projectFee' && field.isEnabled) {
        return {
          fieldName: 'projectFee',
          fieldLabel: getFieldLabel('projectFee', project_fee_label),
          type: FieldType.Number,
          fieldId: FIELD_ID_PROJECT_FEE,
          fieldIndex: field.fieldIndex,
        };
      }

      return null;
    });

    if (fields) {
      return fields.filter(Boolean);
    }

    return [];
  }, [
    arr_label,
    project_fee_label,
    projectFieldsData,
    isProjectFieldsLoading,
    getFieldLabel,
  ]);

  const projectFields = useMemo(
    () =>
      [
        {
          fieldName: 'projectName',
          fieldLabel: getFieldLabel('projectName', project_name_label),
          type: FieldType.Text,
          fieldId: FIELD_ID_PROJECT_NAME,
        },
        {
          fieldName: 'inferredProgress',
          fieldLabel: getFieldLabel(
            'inferredProgress',
            inferred_progress_label,
          ),
          type: FieldType.SingleSelect,
          fieldId: FIELD_ID_INFERRED_PROGRESS,

          optionsAvailable: true,
          metaData: {
            options: InferredProgressOptions(translate),
          },
        },
        {
          fieldName: 'dueDate',
          fieldLabel: getFieldLabel('dueDate', due_date_label),
          type: FieldType.Date,
          fieldId: FIELD_ID_DUE_DATE,
        },
        {
          fieldName: 'startDate',
          fieldLabel: getFieldLabel('startDate', start_date_label),
          type: FieldType.Date,
          fieldId: FIELD_ID_START_DATE,
        },
        {
          fieldLabel: getFieldLabel(
            'lastStatusUpdate',
            last_status_update_label,
          ),
          fieldName: 'lastStatusUpdate',
          type: FieldType.Date,
          fieldId: FIELD_ID_LAST_STATUS_UPDATE,
        },
        {
          fieldLabel: getFieldLabel('progressPercent', progress_percent_label),
          fieldName: ProjectFieldsName.progressPercent,
          type: FieldType.Number,
          fieldId: FIELD_ID_PROGRESS_PERCENT,
        },
        {
          fieldLabel: getFieldLabel('projectAge', project_age_label),
          fieldName: 'projectAge',
          type: FieldType.Number,
          fieldId: FIELD_ID_PROJECT_AGE,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: getFieldLabel('plannedDuration', planned_duration_label),
          fieldName: 'plannedDuration',
          type: FieldType.Number,
          fieldId: FIELD_ID_PLANNED_DURATION,
          metaData: {
            required: true,
          },
        },
        {
          fieldLabel: getFieldLabel(
            'customerMembersInvited',
            customersInvitedLabel,
          ),
          fieldName: 'customerMembersInvited',
          type: FieldType.Number,
          fieldId: FIELD_ID_CUSTOMERS_INVITED,
        },
        {
          fieldLabel: getFieldLabel(
            'customerMembersAccepted',
            customersAcceptedLabel,
          ),
          fieldName: 'customerMembersAccepted',
          type: FieldType.Number,
          fieldId: FIELD_ID_CUSTOMERS_ACCEPTED,
        },
        {
          fieldLabel: getFieldLabel('createdAt', projectCreatedOnLabel),
          fieldName: 'createdAt',
          type: FieldType.Date,
          fieldId: FIELD_ID_CREATED_ON,
        },
        {
          fieldName: 'customerPortalId',
          fieldLabel: translate('templates.customer_portal_singular'),
          type: FieldType.SingleSelect,
          fieldId: FIELD_ID_CUSTOMER_PORTAL_ID,
          fieldType: FieldType.SingleSelect,
          fieldDataType: FieldDataType.Number,
          optionsAvailable: true,
          metaData: {
            options: customerPortals?.map((x) => ({
              label: x.customerPortalName,
              value: x.customerPortalId,
              active: true,
              color: getRandomColor(x?.customerPortalName),
            })),
          },
        },
        {
          fieldLabel: translate('last_milestone_rating'),
          fieldName: ProjectFieldsName.lastMilestoneCSAT,
          type: FieldType.Number,
          fieldId: FIELD_ID_LAST_MILESTONE_CSAT,
        },
        ...arrProjectFee,
        ...(isFinancialsEnabled && !isCurrentUserPartner
          ? [
              {
                fieldLabel: getFieldLabel('projectBudget', projectBudgetLabel),
                fieldName: 'projectBudget',
                type: FieldType.Number,
                fieldId: FIELD_ID_PROJECT_BUDGET,
              },
              ...(!isMultiLineBudgetsEnabled
                ? [
                    {
                      fieldLabel: getFieldLabel(
                        'financialContractType',
                        financialContractTypeLabel,
                      ),
                      fieldName: 'financialContractType',
                      type: FieldType.SingleSelect,
                      fieldId: FIELD_ID_FINANCIAL_CONTRACT_TYPE,
                      optionsAvailable: true,
                      metaData: {
                        options:
                          ProjectFinancialsContractTypeOptions(translate),
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'revenueRecognitionType',
                        revenueRecognitionTypeLabel,
                      ),
                      fieldName: 'revenueRecognitionType',
                      type: FieldType.SingleSelect,
                      fieldId: FIELD_ID_REVENUE_RECOGNITION_TYPE,
                      optionsAvailable: true,
                      metaData: {
                        options: getRevenueRecognitionOptions(
                          translate as Translate<string, string>,
                          customRevenueRecognitionConfigs,
                        ),
                      },
                    },
                  ]
                : []),
              {
                fieldLabel: getFieldLabel('actualRevenue', actualRevenueLabel),
                fieldName: 'actualRevenue',
                type: FieldType.Number,
                fieldId: FIELD_ID_ACTUAL_REVENUE,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: getFieldLabel(
                  'totalRecognisedRevenue',
                  recognisedRevenueLabel,
                ),
                fieldName: 'totalRecognisedRevenue',
                type: FieldType.Number,
                fieldId: FIELD_ID_RECOGNISED_REVENUE,
              },
              {
                fieldLabel: getFieldLabel(
                  'percentageBudgetConsumed',
                  percentageBudgetConsumedLabel,
                ),
                fieldName: 'percentageBudgetConsumed',
                type: FieldType.Number,
                fieldId: FIELD_ID_PERCENTAGE_BUDGET_CONSUMED,
              },
              ...(!isMultiLineBudgetsEnabled
                ? [
                    {
                      fieldLabel: getFieldLabel(
                        'subscriptionFrequency',
                        subscriptionFrequencyLabel,
                      ),
                      fieldName: 'subscriptionFrequency',
                      type: FieldType.SingleSelect,
                      fieldId: FIELD_ID_SUBSCRIPTION_FREQUENCY,
                      optionsAvailable: true,
                      metaData: {
                        options:
                          ProjectFinancialsSubscriptionFrequencyOptions(
                            translate,
                          ),
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'noOfPeriods',
                        numberOfPeriodsLabel,
                      ),
                      fieldName: 'numberOfPeriods',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_NUMBER_OF_PERIODS,
                      metaData: {
                        required: true,
                      },
                    },
                  ]
                : []),
              ...(includeCurrentPeriodDetailsFilterFields &&
              !isMultiLineBudgetsEnabled
                ? [
                    {
                      fieldLabel: getFieldLabel(
                        'currentPeriodNumber',
                        currentPeriodLabel,
                      ),
                      fieldName: 'currentPeriodNumber',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'currentPeriodStartDate',
                        currentPeriodStartDateLabel,
                      ),
                      fieldName: 'currentPeriodStartDate',
                      type: FieldType.Date,
                      fieldId: FIELD_ID_CURRENT_PERIOD_START_DATE,
                      metaData: {
                        required: true,
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'currentPeriodEndDate',
                        currentPeriodEndDateLabel,
                      ),
                      fieldName: 'currentPeriodEndDate',
                      type: FieldType.Date,
                      fieldId: FIELD_ID_CURRENT_PERIOD_END_DATE,
                      metaData: {
                        required: true,
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'periodBudget',
                        periodBudgetLabel,
                      ),
                      fieldName: 'periodBudget',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD_BUDGET,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'periodMinutes',
                        periodBudgetedMinutesLabel,
                      ),
                      fieldName: 'periodMinutes',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD_BUDGET_HOURS,
                      metaData: {
                        required: true,
                      },
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'periodRevenue',
                        currentPeriodRevenueLabel,
                      ),
                      fieldName: 'periodRevenue',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD_REVENUE,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'periodTotalTimeTracked',
                        currentPeriodTrackedHoursLabel,
                      ),
                      fieldName: 'periodTotalTimeTracked',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_CURRENT_PERIOD_TRACKED_HOURS,
                    },
                  ]
                : []),
              ...(canViewCostRates
                ? [
                    {
                      fieldLabel: getFieldLabel('actualCost', actualCostLabel),
                      fieldName: 'actualCost',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_COST,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'actualProfit',
                        actualProfitLabel,
                      ),
                      fieldName: 'actualProfit',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_PROFIT,
                    },
                    {
                      fieldLabel: getFieldLabel(
                        'actualProfitMargin',
                        actualProfitMarginLabel,
                      ),
                      fieldName: 'actualProfitMargin',
                      type: FieldType.Number,
                      fieldId: FIELD_ID_ACTUAL_MARGIN,
                    },
                  ]
                : []),
            ]
          : []),
        ...(isTimeSheetEnabled && !isCurrentUserPartner
          ? [
              {
                fieldLabel: getFieldLabel('totalMinutes', trackedHoursLabel),
                fieldName: ProjectTimeTrackingFieldsName.trackedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_TRACKED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel:
                  locale?.toLowerCase() === 'en'
                    ? budgetHoursLabel
                    : getFieldLabel('budgetedMinutes', budgetHoursLabel),
                fieldName: ProjectTimeTrackingFieldsName.budgetedMinutes,
                type: FieldType.Number,
                fieldId: FIELD_ID_BUDGETED_HOURS,
                metaData: {
                  required: true,
                },
              },
              {
                fieldLabel: getFieldLabel(
                  'remainingMinutes',
                  remainingMinutesLabel,
                ),
                fieldName: ProjectTimeTrackingFieldsName.remainingMinutes,
                type: FieldType.Number,
                fieldId: FIELD_ID_REMAINING_MINUTES,
              },
              {
                fieldLabel: getFieldLabel(
                  'totalAllocatedHours',
                  allocatedHoursLabel,
                ),
                fieldName: ProjectTimeTrackingFieldsName.allocatedHours,
                type: FieldType.Number,
                fieldId: FIELD_ID_ALLOCATED_HOURS,
              },
              {
                fieldLabel: getFieldLabel(
                  'estimateAtCompletion',
                  estimateAtCompletionLabel,
                ),
                fieldName: 'estimateAtCompletion',
                type: FieldType.Number,
                fieldId: FIELD_ID_ESTIMATE_AT_COMPLETION,
              },
              {
                fieldLabel: getFieldLabel(
                  'estimateToComplete',
                  estimateToCompletionLabel,
                ),
                fieldName: 'estimateToComplete',
                type: FieldType.Number,
                fieldId: FIELD_ID_ESTIMATE_TO_COMPLETION,
              },
              {
                fieldLabel: getFieldLabel(
                  'percentageBudgetedHoursConsumed',
                  percentageBudgetedHoursConsumedLabel,
                ),
                fieldName: 'percentageBudgetedHoursConsumed',
                type: FieldType.Number,
                fieldId: FIELD_ID_PERCENTAGE_BUDGETED_HOURS_CONSUMED,
              },
            ]
          : []),
      ].map(
        (x) =>
          ({
            ...x,
            customField: false,
            private: false,
            sourceType: SourceType.Project,
            fieldIndex:
              projectFieldsMapByName[camelCase(x?.fieldName)]?.fieldIndex,
            objectType: FieldSectionType.Project,
          } as FilterableField),
      ),
    [
      getFieldLabel,
      project_name_label,
      inferred_progress_label,
      due_date_label,
      start_date_label,
      last_status_update_label,
      progress_percent_label,
      project_age_label,
      planned_duration_label,
      customersInvitedLabel,
      customersAcceptedLabel,
      projectCreatedOnLabel,
      customerPortals,
      arrProjectFee,
      isFinancialsEnabled,
      isCurrentUserPartner,
      isMultiLineBudgetsEnabled,
      projectBudgetLabel,
      financialContractTypeLabel,
      revenueRecognitionTypeLabel,
      translate,
      customRevenueRecognitionConfigs,
      actualRevenueLabel,
      recognisedRevenueLabel,
      percentageBudgetConsumedLabel,
      subscriptionFrequencyLabel,
      numberOfPeriodsLabel,
      includeCurrentPeriodDetailsFilterFields,
      currentPeriodLabel,
      currentPeriodStartDateLabel,
      currentPeriodEndDateLabel,
      periodBudgetLabel,
      periodBudgetedMinutesLabel,
      currentPeriodRevenueLabel,
      currentPeriodTrackedHoursLabel,
      canViewCostRates,
      actualCostLabel,
      actualProfitLabel,
      actualProfitMarginLabel,
      isTimeSheetEnabled,
      trackedHoursLabel,
      budgetHoursLabel,
      remainingMinutesLabel,
      allocatedHoursLabel,
      estimateAtCompletionLabel,
      estimateToCompletionLabel,
      percentageBudgetedHoursConsumedLabel,
      projectFieldsMapByName,
      locale,
      isMultiLineBudgetsEnabled,
      financialContractTypeField,
    ],
  );

  // Use lite versions of field hooks
  const { templateField, isLoading: isTemplateFieldLoading } =
    useTemplateField(FIELD_ID_TEMPLATE);
  const { projectOwnerField, isLoading: isProjectOwnerFieldLoading } =
    useProjectOwnerFieldLite(FIELD_ID_PROJECT_OWNER); // ← Use lite version!
  const { customerField, isLoading: isCustomerFieldLoading } =
    useCustomerField(FIELD_ID_CUSTOMER);
  const { teamMemberField, isLoading: isTeamMembersFieldLoading } =
    useTeamMembersFieldLite(FIELD_ID_TEAM_MEMBERS); // ← Using lite version!
  const { currentPhasesField, isLoading: isCurrentPhasesFieldLoading } =
    useCurrentPhasesField(FIELD_ID_CURRENT_PHASES);
  const {
    partnerCompaniesField,
    isLoading: isPartnerCompaniesFieldLoading,
    isAnyPartnerCompanyAvailable,
  } = usePartnerCompanyField(FIELD_ID_PARNTER_COMPANIES);
  const { currencyField, isLoading: isCurrencyFieldLoading } =
    useCurrencyField(FIELD_ID_CURRENCY);

  const fields = useMemo(
    () =>
      [
        ...projectFields,
        templateField,
        projectOwnerField,
        customerField,
        teamMemberField,
        currentPhasesField,
        isAnyPartnerCompanyAvailable ? partnerCompaniesField : undefined,
        currencyField,
      ].filter((x) => Boolean(x)) as FilterableField[],
    [
      projectFields,
      templateField,
      projectOwnerField,
      customerField,
      teamMemberField,
      currentPhasesField,
      isAnyPartnerCompanyAvailable,
      partnerCompaniesField,
      currencyField,
    ],
  );

  const isFieldsLoading = useMemo(
    () =>
      isProjectOwnerFieldLoading ||
      isCustomerFieldLoading ||
      isTeamMembersFieldLoading ||
      isCurrentPhasesFieldLoading ||
      isTemplateFieldLoading ||
      isPartnerCompaniesFieldLoading ||
      isCurrencyFieldLoading,
    [
      isProjectOwnerFieldLoading,
      isCustomerFieldLoading,
      isTeamMembersFieldLoading,
      isCurrentPhasesFieldLoading,
      isTemplateFieldLoading,
      isPartnerCompaniesFieldLoading,
      isCurrencyFieldLoading,
    ],
  );

  // Filter fields based on includedFieldNames if provided
  const filteredFields = useMemo(() => {
    // If includedFieldNames is undefined, return all fields (default behavior)
    if (includedFieldNames === undefined) {
      return fields;
    }

    // If includedFieldNames is empty array, return no fields
    if (includedFieldNames.length === 0) {
      return [];
    }

    // Filter to only included fields (case-insensitive comparison)
    const allowedSet = new Set(includedFieldNames.map((n) => n.toLowerCase()));

    return fields.filter((f) => allowedSet.has(f.fieldName.toLowerCase()));
  }, [fields, includedFieldNames]);

  return useMemo(
    () => ({
      fields: filteredFields,
      loading: isFieldsLoading,
    }),
    [filteredFields, isFieldsLoading],
  );
};
